/**
 * /app/trusts/$id/audit — Per-trust audit log, trust-scoped (route shell).
 *
 * Screen impl in `./audit-screen.tsx`, lazy-loaded. This is a trust sub-tab, not
 * a first-paint route (the trust overview + dashboard stay eager), and the
 * hash-chain UI + CSV builder are sizeable — so it leaves the initial bundle.
 */

import { createFileRoute, lazyRouteComponent } from '@tanstack/react-router';

/** Deep-link search params (feature backlog #40). A link such as
 *  `/app/trusts/$id/audit?filter=resolutions&q=signed` lands on the audit tab
 *  with the category chip pre-selected and the free-text search pre-filled, so
 *  a notification/bell link can point a user straight at the relevant events.
 *  Defined here (not in the lazily-loaded screen) so the validator runs before
 *  the screen chunk loads. Permissive: unknown values fall back to safe
 *  defaults rather than throwing the route. */
const AUDIT_FILTER_IDS = [
  'all',
  'resolutions',
  'documents',
  'beneficiaries',
  'amendments',
  'fica',
] as const;
export type AuditDeepLinkFilter = (typeof AUDIT_FILTER_IDS)[number];

export interface AuditSearch {
  /** Optional so existing typed `<Link to="…/audit">` callers don't have to
   *  pass it; the screen defaults an absent filter to 'all'. Only the explicit
   *  deep-link values survive validation. */
  filter?: AuditDeepLinkFilter;
  q?: string;
}

function validateAuditSearch(search: Record<string, unknown>): AuditSearch {
  const out: AuditSearch = {};
  const rawFilter = search['filter'];
  if (AUDIT_FILTER_IDS.includes(rawFilter as AuditDeepLinkFilter)) {
    out.filter = rawFilter as AuditDeepLinkFilter;
  }
  const rawQ = search['q'];
  if (typeof rawQ === 'string' && rawQ.trim().length > 0 && rawQ.length <= 200) {
    out.q = rawQ.trim();
  }
  return out;
}

export const Route = createFileRoute('/app/trusts/$id/audit')({
  component: lazyRouteComponent(() => import('./audit-screen'), 'AuditScreen'),
  validateSearch: validateAuditSearch,
});
